'use client';

import { useEffect, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import type { AssetsSearchGroupBy } from '@terra/shared-contracts-assets';
import { ListPageOptionsSwitch } from '@terra/ui-design-system';
import { ListPageGroupByOptionsMenu } from '../../common/ListPageGroupByOptionsMenu';
import {
  ASSETS_VIEW,
  useTabScopedAssetsFilters,
} from '../../../store/tab/assetsTabScopedFilterStore';
import { useArchivedLifecycleFilter } from '../../../hooks/useArchivedLifecycleFilter';
import type { AssetsSearchRequestFilters } from '../../../hooks/api/assets-search-request';
import { AssetsExportMenuRow } from './AssetsExportMenuRow';

type AssetsViewOptionsMenuProps = {
  groupByOptions: readonly AssetsSearchGroupBy[];
  groupBy: AssetsSearchGroupBy | undefined;
  onGroupByChange: (groupBy: AssetsSearchGroupBy | null) => void;
  areAllGroupsCollapsed: boolean;
  onExpandAllGroups: () => void;
  onCollapseAllGroups: () => void;
  applicationId: string | null | undefined;
  exportFilters: AssetsSearchRequestFilters;
};

export function AssetsViewOptionsMenu({
  groupByOptions,
  groupBy,
  onGroupByChange,
  areAllGroupsCollapsed,
  onExpandAllGroups,
  onCollapseAllGroups,
  applicationId,
  exportFilters,
}: AssetsViewOptionsMenuProps) {
  const t = useTranslations('proxy.assets.table');
  const tViewOptions = useTranslations('proxy.assets.viewOptions');
  const {
    view,
    detectionView,
    setDetectionView,
    lifecycleState,
    setLifecycleState,
  } = useTabScopedAssetsFilters();
  const { isLifecycleUiEnabled } = useArchivedLifecycleFilter({
    lifecycleState,
  });

  useEffect(() => {
    if (lifecycleState === 'archived') {
      setLifecycleState('all');
    }
  }, [lifecycleState, setLifecycleState]);
  const options = useMemo(
    () => groupByOptions.map((option) => ({ value: option, label: t(option) })),
    [groupByOptions, t],
  );

  return (
    <ListPageGroupByOptionsMenu
      options={options}
      groupByField={groupBy ?? null}
      onGroupByFieldChange={onGroupByChange}
      areAllGroupsCollapsed={areAllGroupsCollapsed}
      onExpandAllGroups={onExpandAllGroups}
      onCollapseAllGroups={onCollapseAllGroups}
      footer={
        view === ASSETS_VIEW.UNVERIFIED ? null : (
          <AssetsExportMenuRow
            applicationId={applicationId}
            filters={exportFilters}
          />
        )
      }
    >
      {view === ASSETS_VIEW.ENDPOINTS ? (
        <ListPageOptionsSwitch
          label={tViewOptions('hideDoneDetections')}
          checked={detectionView === 'active'}
          onCheckedChange={(checked) =>
            setDetectionView(checked ? 'active' : 'all')
          }
        />
      ) : null}
      {isLifecycleUiEnabled ? (
        <ListPageOptionsSwitch
          label={tViewOptions('hideArchivedEndpoints')}
          checked={lifecycleState === 'active'}
          onCheckedChange={(checked) =>
            setLifecycleState(checked ? 'active' : 'all')
          }
        />
      ) : null}
    </ListPageGroupByOptionsMenu>
  );
}
